import React, { useCallback, useState } from "react";
import { updateDataset } from "@/lib/api/datasets";
import { DescriptionSection } from "@/components/main/datasets/DescriptionSection";
import { showNotificationGlobal } from "@/stores/useNotificationStore";

interface DatasetDescriptionProps {
  datasetId: string;
  /// The persisted description (owned by the parent row / page).
  description: string | null;
  /// Owners get the add/edit affordances; viewers see a read-only caption.
  canEdit: boolean;
  /// Bubble the saved value up so the row/page keep the single latest copy.
  onSaved: (description: string | null) => void;
  /// Right padding, forwarded to the section shell (defaults to the dataset-row
  /// `pr-2`; the row passes `pr-0` to align the add pill with the search bar).
  pr?: string;
}

/// Dataset description, backed by the datasets API. Presentation + edit
/// interaction live in the reusable `DescriptionSection`.
export const DatasetDescription: React.FC<DatasetDescriptionProps> = ({
  datasetId,
  description,
  canEdit,
  onSaved,
  pr,
}) => {
  const [status, setStatus] = useState<null | "saving" | "error">(null);

  const onSave = useCallback(
    async (next: string | null) => {
      setStatus("saving");
      try {
        await updateDataset(datasetId, { description: next });
        onSaved(next);
        setStatus(null);
      } catch {
        setStatus("error");
        showNotificationGlobal("Failed to save description", "error");
      }
    },
    [datasetId, onSaved],
  );

  return (
    <DescriptionSection
      description={description}
      canEdit={canEdit}
      onSave={(next) => void onSave(next)}
      saving={status === "saving"}
      error={status === "error"}
      pr={pr}
      collapsible
    />
  );
};
